import { useEffect, useState } from 'react';
import { CalendarCog, CalendarX2 } from 'lucide-react';
import { eventsApi, ValidationFailure } from '../api/events';
import type { EventDetail, FieldErrors } from '../api/events';
import { useEventTypes } from '../hooks/useEventTypes';
import { toWallClockInZone } from '../lib/datetime';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { TextAreaField } from '../ui/Field';
import {
  emptyEventForm,
  eventFormPayload,
  allRequired,
  EventFormFields,
  EventRequirementFields,
  incompleteReason,
  normaliseFieldErrors,
} from './EventForm';
import type { EventFormValues, EventRequires } from './EventForm';

/**
 * Correcting an event.
 *
 * This is the screen the product's claim rests on: the answer to "what time do we leave" is a
 * field on a record, and a field on a record is something one person fixes in one place — not a
 * correction message that everybody has to scroll back past the original to find.
 *
 * The form opens on what the leader typed, which means turning the stored instants back into
 * wall-clock readings **in the event's own zone**. A campout in Colorado run by a troop in New
 * Hampshire must open on 5pm Mountain, not on the 7pm the editor's laptop would render, or saving
 * an unrelated change would move the whole trip.
 */
export function EditEventScreen({
  unitId,
  eventId,
  onSaved,
  onCancel,
}: {
  unitId: string;
  eventId: string;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [values, setValues] = useState<EventFormValues>(emptyEventForm);

  // Hydrated off the event below. All four on until then, so a slow load cannot flash the boxes
  // unticked and have somebody save an event that asks nothing.
  const [requires, setRequires] = useState<EventRequires>(allRequired);
  const eventTypes = useEventTypes(unitId);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;

    eventsApi
      .detail(unitId, eventId)
      .then((loaded) => {
        if (!live) return;
        setEvent(loaded);

        const zone = loaded.timeZoneId;
        setValues({
          // The type it already carries, even if the unit has since archived it. EventFormFields
          // puts that back in the picker so an unrelated save does not silently clear it.
          eventTypeId: loaded.eventTypeId ?? '',
          title: loaded.title,
          description: loaded.description ?? '',
          locationName: loaded.locationName ?? '',
          locationAddress: loaded.locationAddress ?? '',
          startsAt: toWallClockInZone(loaded.startsAt, zone),
          endsAt: toWallClockInZone(loaded.endsAt, zone),
          departsAt: loaded.departsAt ? toWallClockInZone(loaded.departsAt, zone) : '',
          returnsAt: loaded.returnsAt ? toWallClockInZone(loaded.returnsAt, zone) : '',
        });

        // Off the event, never re-derived from the kind. §9: a type is a source of copies taken
        // once, so refiling an event under a different kind here changes the kind and nothing
        // else -- the same rule the picker's comment below states for the address.
        setRequires({
          rsvp: loaded.requiresRsvp,
          forms: loaded.requiresForms,
          packing: loaded.requiresPacking,
          travel: loaded.requiresTravel,
          program: loaded.requiresProgram,
        });
      })
      .catch(() => live && setLoadError('Could not load this event.'));

    return () => {
      live = false;
    };
  }, [unitId, eventId]);

  const change = (patch: Partial<EventFormValues>) => setValues((v) => ({ ...v, ...patch }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!event) return;

    setBusy(true);
    setFieldErrors({});
    setError(null);

    try {
      await eventsApi.update(unitId, eventId, {
        ...eventFormPayload(values, requires),
        // The zone the readings above were typed in, sent back exactly as it came. The API stores
        // it as an override only when it differs from the unit's, so this round-trips an event in
        // another zone rather than quietly pulling it home.
        timeZoneId: event.timeZoneId,
      });
      onSaved();
    } catch (e) {
      if (e instanceof ValidationFailure) {
        setFieldErrors(normaliseFieldErrors(e.errors));
      } else {
        setError(e instanceof Error ? e.message : 'Could not save the change.');
      }
      setBusy(false);
    }
  }

  return (
    <Page>
      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p
              className="t-eyebrow inline-flex items-center gap-2"
              style={{ color: 'var(--color-accent-announcement-text)', margin: 0 }}
            >
              <CalendarCog size={14} aria-hidden="true" />
              {event?.unit.name}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 0' }}
            >
              Edit event
            </h1>
            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 0', maxWidth: '46ch' }}
            >
              Change it here and every family reads the new time. The thread stays where it is.
            </p>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          {loadError && (
            <p
              className="t-body"
              role="alert"
              style={{ color: 'var(--color-text-secondary)', paddingBlock: 'var(--space-6)' }}
            >
              {loadError}
            </p>
          )}

          {event && (
            <form onSubmit={submit} style={{ paddingBlock: 'var(--space-6)', maxWidth: '40rem' }}>
              <div className="flex flex-col" style={{ gap: 'var(--space-5)' }}>
                <EventFormFields
                  values={values}
                  onChange={change}
                  fieldErrors={fieldErrors}
                  eventTypes={eventTypes}
                  // No prefill on an edit, deliberately. Every box already holds something a
                  // leader decided, and refiling an event under a different kind must not
                  // overwrite the address forty families have already read. Changing the kind
                  // here changes the kind and nothing else.
                  onChooseType={(eventTypeId) => change({ eventTypeId })}
                  zoneNote={`Times below are ${event.timeZoneId.replace(/_/g, ' ')} — this event’s time zone, whatever zone you are in right now. Set in quarter hours.`}
                />

                <EventRequirementFields
                  requires={requires}
                  onChange={(patch) => setRequires((r) => ({ ...r, ...patch }))}
                />

                {error && (
                  <p
                    className="t-body"
                    role="alert"
                    style={{
                      background: 'var(--color-status-blocked-surface)',
                      border: '1px solid var(--color-status-blocked)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-3)',
                      color: 'var(--color-text-primary)',
                      margin: 0,
                    }}
                  >
                    {error}
                  </p>
                )}

                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" type="submit" disabledReason={incompleteReason(values)}>
                    {busy ? 'Saving…' : 'Save changes'}
                  </Button>

                  <Button variant="secondary" onClick={onCancel}>
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          )}

          {/* Below the form and outside it, because it is a different act with a different verb.
              Putting it inside would make one submit button ambiguous and put a destructive
              action one stray Enter away from a leader fixing a typo. */}
          {event && (
            <div style={{ paddingBottom: 'var(--space-7)', maxWidth: '40rem' }}>
              <CallItOff
                unitId={unitId}
                eventId={eventId}
                alreadyCancelled={event.status === 'Cancelled'}
                cancellationReason={event.cancellationReason}
                onCancelled={onSaved}
              />
            </div>
          )}
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

/**
 * Calling the event off.
 *
 * Two steps rather than one, and the second step is the reason itself. A confirmation dialog that
 * only asks "are you sure" adds a click and no information; typing why the campout is off is both
 * the confirmation and the thing forty families are about to read. There is no browser `confirm`
 * anywhere in this product for the same reason.
 *
 * Not offered at all once the event is off. A leader arriving second sees what the first one
 * wrote, which is the answer they came for — an idempotent button would be honest and useless.
 */
function CallItOff({
  unitId,
  eventId,
  alreadyCancelled,
  cancellationReason,
  onCancelled,
}: {
  unitId: string;
  eventId: string;
  alreadyCancelled: boolean;
  cancellationReason: string | null;
  onCancelled: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (alreadyCancelled) {
    return (
      <p
        className="t-body inline-flex items-start gap-2"
        style={{ color: 'var(--color-text-secondary)', margin: 0 }}
      >
        <CalendarX2 size={16} aria-hidden="true" style={{ marginTop: '0.2em', flexShrink: 0 }} />
        <span>
          This event has been called off
          {cancellationReason ? ` — ${cancellationReason}` : ''}. The details above can still be
          corrected; families read them alongside the cancellation.
        </span>
      </p>
    );
  }

  if (!open) {
    return (
      <button type="button" className="link-button t-body" onClick={() => setOpen(true)}>
        <span className="inline-flex items-center gap-2">
          <CalendarX2 size={14} aria-hidden="true" />
          Call this event off
        </span>
      </button>
    );
  }

  return (
    <div
      style={{
        background: 'var(--color-status-blocked-surface)',
        border: '1px solid var(--color-status-blocked)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
      }}
    >
      <p
        className="t-body-strong inline-flex items-center gap-2"
        style={{ color: 'var(--color-text-primary)', margin: '0 0 var(--space-2)' }}
      >
        <CalendarX2 size={16} aria-hidden="true" />
        Call this event off
      </p>
      <p
        className="t-caption"
        style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)', maxWidth: '46ch' }}
      >
        It stays on the calendar, marked cancelled, and the thread stays open. Families who already
        made plans need to see that it was called off rather than find it gone.
      </p>

      <TextAreaField
        label="Why it is off"
        required
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
        error={error ?? undefined}
        hint="Rained off, no drivers, the gym is double-booked — whatever families need to know."
      />

      <div className="flex flex-wrap gap-3" style={{ marginTop: 'var(--space-4)' }}>
        <Button
          variant="destructive"
          disabledReason={reason.trim() ? undefined : 'Say why it is off first.'}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await eventsApi.cancel(unitId, eventId, reason.trim());
              onCancelled();
            } catch (e) {
              setError(
                e instanceof ValidationFailure
                  ? (e.errors.reason?.[0] ?? e.message)
                  : e instanceof Error
                    ? e.message
                    : 'Could not call it off.',
              );
              setBusy(false);
            }
          }}
        >
          {busy ? 'Calling it off…' : 'Call it off'}
        </Button>

        <Button variant="secondary" onClick={() => setOpen(false)}>
          Keep it on
        </Button>
      </div>
    </div>
  );
}
